export const EXPORT_JOB_STATUS = {
  PENDING: 'pending',
  PROCESSING: 'processing',
  COMPLETED: 'completed',
  FAILED: 'failed',
} as const

export type ExportJobStatus = (typeof EXPORT_JOB_STATUS)[keyof typeof EXPORT_JOB_STATUS]

export const CRT_EXPORT_TYPES = [
  'ip_accrual_details',
  'ip_accrual_raw',
  'sales_deal_actuals_details',
  'sales_deal_actuals_raw',
  // P&L summary exports. Their rows come from the P&L forecast build, not a paged query, so the
  // worker computes them whole (see `PNL_EXPORT_TYPES`).
  'pnl_export_view',
  'pnl_domestic',
  'pnl_cdf',
] as const
export type CrtExportType = (typeof CRT_EXPORT_TYPES)[number]

/** The P&L summary export types, whose rows the worker computes in memory from the P&L build. */
export const PNL_EXPORT_TYPES = [
  'pnl_export_view',
  'pnl_domestic',
  'pnl_cdf',
] as const satisfies readonly CrtExportType[]
export type PnlExportType = (typeof PNL_EXPORT_TYPES)[number]

// One per SAP extract the credit module imports. Each export replays the table in the exact
// import layout so the file can be edited and re-uploaded as-is.
export const CREDIT_EXPORT_TYPES = [
  'sap_customers',
  'sap_customer_companies',
  'sap_partner_functions',
  'sap_sales_orders',
  'sap_billing_documents',
  'sap_billing_document_details',
  'sap_customer_transactions',
  'sap_customer_partner_credit_segments',
] as const
export type CreditExportType = (typeof CREDIT_EXPORT_TYPES)[number]

// Every export type across modules. Each module keeps its own `export_jobs` table
// (`fuze_crt.export_jobs`, `fuze_credit.export_jobs`) and its own "My Exports" router; the type
// decides which table a job lives in, see `getExportTypeModule`.
export const EXPORT_TYPES = [...CRT_EXPORT_TYPES, ...CREDIT_EXPORT_TYPES] as const
export type ExportType = (typeof EXPORT_TYPES)[number]

// The modules that produce exports. Core has no export queue, so this is narrower than the
// `SwitchableModule` union in @repo/permissions (which this package cannot import without a cycle).
export const EXPORT_MODULES = ['crt', 'credit'] as const
export type ExportModule = (typeof EXPORT_MODULES)[number]

export const EXPORT_TYPE_MODULES: Record<ExportType, ExportModule> = {
  ...(Object.fromEntries(CRT_EXPORT_TYPES.map((type) => [type, 'crt'])) as Record<
    CrtExportType,
    'crt'
  >),
  ...(Object.fromEntries(CREDIT_EXPORT_TYPES.map((type) => [type, 'credit'])) as Record<
    CreditExportType,
    'credit'
  >),
}

/** The module whose `export_jobs` table holds jobs of this type. */
export function getExportTypeModule(exportType: ExportType): ExportModule {
  return EXPORT_TYPE_MODULES[exportType]
}

export const EXPORT_JOB_NAME_PREFIX = 'export-job-'

export function getExportJobName(exportJobId: number): string {
  return `${EXPORT_JOB_NAME_PREFIX}${exportJobId}`
}

// The batch size used when streaming rows out of the database into the CSV writer.
// Keeps worker memory bounded regardless of total row count.
export const EXPORT_QUERY_BATCH_SIZE = 50_000

// How long a generated export's presigned download URL stays valid (seconds).
export const EXPORT_DOWNLOAD_URL_EXPIRY_SECONDS = 90

// How long a generated export file is retained before it is eligible for cleanup (days).
export const EXPORT_RETENTION_DAYS = 7

// How long a non-terminal (pending/processing) export row may sit untouched before cleanup treats it
// as orphaned and reclaims it (hours). Only the start-of-processing and completion updates touch
// `updated_at`, so this must comfortably exceed the longest plausible generation time to never reclaim
// a genuinely in-flight export — it is a last-resort backstop for jobs that never reached a terminal
// state (e.g. the worker crashed before its `failed` handler could mark the row failed).
export const EXPORT_STALLED_CUTOFF_HOURS = 24
